import { GridOptions, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";

import { NavFns } from "@/shared/nav-fns";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { UniAgFns } from "@/shared/uni-ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { takeUntil } from "rxjs/operators";
import { Account, CreditMemoTypeEnum, InvoiceHeader, InvoiceStatus } from "../model/entities/entity-model";
import { InvoiceStatusCode } from "../model/enums/invoice-status-code";
import { DomainBaseComponent, wrapBusy } from "../shared/domain-base.component";
import { DomainService } from "../shared/domain.service";
import { EditAccountDialogComponent } from "./edit-account-dialog.component";
import { InvoiceDialogComponent } from "../shared/invoice-dialog.component";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { RestockFeeDialogComponent, RestockFeeDialogParams } from "./restock-fee-dialog.component";
import { groupBy } from "lodash";

@Component({
  selector: "app-account",
  templateUrl: "./account.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AccountComponent extends DomainBaseComponent implements OnInit {
  accGridOptions: GridOptionsExt;
  invhGridOptions: GridOptionsExt;
  cmGridOptions: GridOptionsExt;
  invoiceHeaders: InvoiceHeader[] = [];
  selectedAccount: Account | null = null;

  constructor(protected domainService: DomainService, protected route: ActivatedRoute) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.accGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAccGridReady,
      onRowSelected: this.onRowSelected,
      onFilterChanged: this.onFilterChanged,
      rowModelType: "serverSide",
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.replaceLocation();
        }
      },
    });
    // AgFns.captureGridRouteParams(this.accGridOptions, this.route, "id");
    this.captureLocation(this.accGridOptions, "id");

    this.invhGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onInvhGridReady,
      rowModelType: "serverSide",
    });

    this.cmGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onCmGridReady,
      rowModelType: "serverSide",
      masterDetail: true,
      isRowMaster: (data: any) => !!data,
    });
    this.configureCmDetailGrid();

    this.isPageReady = true;
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(Account);
    return true;
  }

  // This method is called AFTER ngAfterViewInit - this is important
  // because the ColDef's here reference TemplateRef's above that will not be resolved until here.
  onAccGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...AgFns.createButtonProps("", this.editAccount.bind(this), { label: "Edit" }), ...AgFns.fixWidth(100) },
      {
        headerName: "Name",
        field: "accountName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      {
        ...UniAgFns.getActiveStatusColDef("activeStatus.name"),
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth("status"),
      },

      {
        headerName: "Start Date",
        field: "startDate",
        filter: "agDateColumnFilter",
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "GL Account Ref",
        field: "gLAccountRef",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Invoice Doc Submit Type",
        field: "invoiceDocSubmitType.name",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(200),
      },
      {
        headerName: "Invoice and Manifest Email",
        field: "invoiceEmail",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Due in # of days",
        field: "dueDateNumDays",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth(180),
      },
      {
        ...AgFns.createCheckboxProps("Managed Inventory?", "allowReissuanceOrders"),
        editable: false,
        ...AgFns.fixWidth(180),
      },
      {
        ...AgFns.createCheckboxProps("Allow Partial Shipments?", "allowPartialShipments"),
        editable: false,
        ...AgFns.fixWidth(200),
      },
      {
        headerName: "GL Contact Id",
        field: "gLContactId",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(180),
      },
    ];

    const sortModel = [{ colId: "accountName", sort: "asc" as const }];

    // next line is needed by the buildDatasource method
    AgFns.initGrid(event, colDefs, sortModel);

    this.updateDatasource();

    this.applyLocation();
  }

  onInvhGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps("Invoice", this.openInvoiceDialog.bind(this), {
          calcLabel: (row: InvoiceHeader) => row.id,
        }),
        field: "id",
        sortable: true,
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(180),
      },
      {
        ...AgFns.createButtonProps("Reprint", this.reprintInvoice.bind(this), {
          label: "Invoice",
          canDisplay: (row: InvoiceHeader) => row?.invoiceStatusId === InvoiceStatusCode.InvoicePosted,
        }),
        ...AgFns.fixWidth(110),
      },
      {
        ...AgFns.createButtonProps("Reprint", this.reprintPackingSlip.bind(this), {
          label: "Packing Document",
          canDisplay: (row: InvoiceHeader) => row?.invoiceStatusId === InvoiceStatusCode.InvoicePosted,
        }),
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Account",
        valueGetter: () => this.selectedAccount?.accountName ?? "",
        filter: false,
        sortable: false,
        ...AgFns.flexWidth(200),
      },
      {
        headerName: "Invoice Date",
        field: "invoiceDate",
        filter: "agDateColumnFilter",
        sortable: true,
        ...AgFns.fixWidth(180),
      },
      {
        headerName: "Total",
        valueGetter: (params: any) => {
          const d = params.data;
          if (!d) return null;
          return +((d.subtotalAmt ?? 0) + (d.taxAmt ?? 0) + (d.freightAmt ?? 0) + (d.orderHandlingAmt ?? 0) - (d.prepaymentAmt ?? 0) - (d.pCardAmt ?? 0)).toFixed(2);
        },
        valueFormatter: AgFns.currencyFormatter,
        type: "numericColumn",
        filter: false,
        sortable: false,
        ...AgFns.fixWidth("money"),
      },
      {
        ...AgFns.createSetFilterObjectProps("Status", "invoiceStatus.name", this.dbQueryService.getAllCached(InvoiceStatus)),
        sortable: true,
      },
      {
        headerName: "Job Order",
        ...NavFns.createIdCellClickedNavProps("joHeaderId", this.router, "/in-process/manage/job-orders"),
        filter: "agNumberColumnFilter",
        sortable: true,
        type: "leftAligned",
        ...AgFns.fixWidth(150),
      },
    ];

    const sortModel = [{ colId: "id", sort: "desc" as const }];

    AgFns.initGrid(event, colDefs, sortModel);
    this.updateDatasource();
  }

  reprintInvoice(invh: InvoiceHeader) {
    if (invh?.id) {
      this.router.navigate(["/printable-invoice", invh.id]);
    }
  }

  reprintPackingSlip(invh: InvoiceHeader) {
    if (invh?.id) {
      this.router.navigate(["/printable-packing-slip", invh.id]);
    }
  }

  onCmGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Credit Memo Id",
        field: "id",
        type: "leftAligned",
        filter: "agNumberColumnFilter",
        cellRenderer: "agGroupCellRenderer",
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Type",
        valueGetter: (params: any) => {
          const t = params.data?.creditMemoTypeId as CreditMemoTypeEnum | undefined;
          if (t == null) return "";
          if (t === CreditMemoTypeEnum.Memo) return "Credit Memo";
          if (t === CreditMemoTypeEnum.PrepaymentRefund) return "Prepayment Refund";
          if (t === CreditMemoTypeEnum.PcardRefund) return "P-Card Refund";
          return params.data?.creditMemoType?.name ?? "";
        },
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(180),
      },
      {
        headerName: "Status",
        field: "creditMemoStatus.name",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Amount",
        field: "creditMemoAmt",
        valueFormatter: AgFns.currencyFormatter,
        type: "numericColumn",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth("money"),
      },
      {
        headerName: "Original Charge",
        field: "transactionId",
        type: "leftAligned",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Refund TransId",
        field: "refundTransactionId",
        type: "leftAligned",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Job Order",
        ...NavFns.createIdCellClickedNavProps("joHeaderId", this.router, "/in-process/manage/job-orders"),
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Return Detail",
        field: "returnRequestDetailId",
        type: "leftAligned",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Notes",
        field: "notes",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(240),
      },
    ];

    const sortModel = [{ colId: "id", sort: "desc" as const }];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  /** Inline master-detail: each CreditMemo expands to its CreditMemoLedgerEntry
   *  rows (Product Credit, Restocking Charge, future kinds). Entries are loaded
   *  with the parent via .expand() — no extra server round-trip. Sign convention:
   *  Amount is stored as the operator sees it (credits +, charges −) so values
   *  render directly. */
  private configureCmDetailGrid() {
    const opts = this.cmGridOptions;
    if (!opts) return;

    const detailGridOptions: GridOptions = {
      defaultColDef: { resizable: true, sortable: false, filter: false },
      columnDefs: [
        {
          headerName: "Line",
          valueGetter: (p: any) => this.formatLedgerKind(p.data?.kindId),
          ...AgFns.flexWidth(220),
        },
        {
          headerName: "Amount",
          field: "amount",
          valueFormatter: AgFns.currencyFormatter,
          type: "numericColumn",
          headerClass: "ag-right-aligned-header",
          cellClass: "cell-numeric",
          ...AgFns.fixWidth("money"),
        },
        {
          headerName: "Notes",
          field: "notes",
          ...AgFns.flexWidth(280),
        },
      ],
    };

    opts.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params: any) => {
        const cm = params.data;
        const entries = (cm?.creditMemoLedgerEntries ?? []) as any[];
        params.successCallback(entries);
      },
    };
  }

  private formatLedgerKind(kindId: number | null | undefined): string {
    if (kindId == null) return "";
    if (kindId === 0) return "Product Credit";
    if (kindId === 1) return "Restocking Charge";
    return `Kind ${kindId}`;
  }

  updateDatasource() {
    const gridApi = this.accGridOptions.apiX;
    if (gridApi == null) {
      return;
    }

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createAccountQuery(null));
    gridApi.setGridOption("serverSideDatasource", ds);

    this.applyLocation();
  }

  updateInvhDatasource(accountId: number) {
    const gridApi = this.invhGridOptions.apiX;
    if (gridApi == null) {
      return;
    }

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createInvoicesForAccountQuery(accountId));
    gridApi.setGridOption("serverSideDatasource", ds);
  }

  updateCmDatasource(accountId: number) {
    const gridApi = this.cmGridOptions?.apiX;
    if (gridApi == null) {
      return;
    }

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createCreditMemosForAccountQuery(accountId));
    gridApi.setGridOption("serverSideDatasource", ds);
  }

  @wrapBusy
  async onRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    let account = e.data as Account;
    if (!account) {
      return;
    }
    this.selectedAccount = account;
    this.updateInvhDatasource(account.id);
    this.updateCmDatasource(account.id);
    this.replaceLocation(account.id);
  }

  onFilterChanged() {
    this.replaceLocation();
  }

  async editAccount(account: Account) {
    await DialogBaseComponent.open(EditAccountDialogComponent, this, {
      account: account,
    });
    this.accGridOptions.apiX.redrawRows();
  }

  async openInvoiceDialog(invh: InvoiceHeader) {
    await InvoiceDialogComponent.openDialog(this.pngDialogService, { invoiceId: invh.id });
  }

  @wrapBusy
  async exportInvoicesToExcel() {
    if (!this.selectedAccount) return;

    // Fetch all invoices for the selected account
    const query = this.dbQueryService.createInvoicesForAccountQuery(this.selectedAccount.id);
    const invoices = await query.execute() as InvoiceHeader[];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Invoices");
    const exportDate = new Date();

    // Title row
    worksheet.addRow([`${this.selectedAccount.accountName} - Invoices`]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:F1");

    // Export timestamp
    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:F2");

    worksheet.addRow([]);

    // Header row
    worksheet.addRow(["Invoice #", "Account", "Invoice Date", "Total", "Status", "Job Order"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    // Column widths
    worksheet.getColumn(1).width = 15;
    worksheet.getColumn(2).width = 30;
    worksheet.getColumn(3).width = 15;
    worksheet.getColumn(4).width = 15;
    worksheet.getColumn(5).width = 15;
    worksheet.getColumn(6).width = 15;

    // Data rows
    invoices.forEach((inv) => {
      worksheet.addRow([
        inv.id,
        inv.joHeader?.account?.accountName || "",
        inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : "",
        (inv.subtotalAmt ?? 0) + (inv.taxAmt ?? 0) + (inv.freightAmt ?? 0) + (inv.orderHandlingAmt ?? 0) - (inv.prepaymentAmt ?? 0) - (inv.pCardAmt ?? 0),
        inv.invoiceStatus?.name || "",
        inv.joHeaderId,
      ]);
    });

    // Format currency column
    for (let row = 5; row <= worksheet.rowCount; row++) {
      worksheet.getCell(`D${row}`).numFmt = "$#,##0.00";
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const fileName = `${this.selectedAccount.accountName.replace(/[^a-zA-Z0-9]/g, "_")}_Invoices_${exportDate.toISOString().split("T")[0]}.xlsx`;
    saveAs(blob, fileName);
  }

  /** Create an invoice containing unbilled restocking fees for the selected account */
  async createRestockInvoice() {
    if (!this.selectedAccount) return;

    const r = await this.dbQueryService.getReturnRequestDetailsForRestockFee(this.selectedAccount.id);

    if (!r.length) {
      await this.dialogService.showOkMessage("Restock Fees", `There are no outstanding restock fees for ${this.selectedAccount.accountName} at this time.`);
      return;
    }

    // group details by billing address & PO number - each is on a separate invoice
    const dict = groupBy(r, x => this.UtilFns.getAddress(x.joDetail.joHeader, 'billing') + '\r\n' + x.joDetail.joHeader.customerPurchaseOrderNumber);

    const groups = Object.values(dict);
    let i = 1;
    for (const group of groups) {
      await RestockFeeDialogComponent.open<RestockFeeDialogParams>(RestockFeeDialogComponent, this, {
        account: this.selectedAccount,
        returnRequestDetails: group,
        index: i++,
        totalCount: groups.length
      });     
    }
  }
}
